import { Box, Button, Flex, Icon, Text } from '@chakra-ui/react'
import { UsersRound } from 'lucide-react'
import PersonCell, { personTone } from './base/PersonCell'
import FastAvatar from '../image/FastAvatar'
import React, { useContext, useMemo, useState } from 'react'
import { text } from '../../themes/styles'
import { CircleFlag } from 'react-circle-flags'
import { useTranslation } from 'react-i18next'
import { PlusIcon, Trash04Icon } from '../../assets/icons'
import { getCountryNameByCode, phoneNumbers } from '../countries/PhoneDial'
import DiscardModal from '../modals/discardModal'
import AddTeamsModal from '../modals/addTeams'
import FilterButton from './base/FilterButton'
import MasterTable, { Column, RowData } from './base/MasterTable'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { deleteTeam, fetchTeamList } from '../../api/workforce/workforce'
import { AuthContext } from '../../hooks/auth/useAuth'
import { useNavigate } from 'react-router-dom'
import { prefetchTeamRowIntent } from '../../lib/performance/prefetch'
import TableLayout from '../common/TableLayout'
import { useTableState } from '../../hooks/useTableState'

interface TableComponentProps {
  setData: React.Dispatch<React.SetStateAction<RowData[]>>
  isUserMode?: boolean
}

/** Members plus the manager, counting a manager who is also a member only once. */
const teamHeadcount = (team: { members?: Array<string | number | { id: string | number }>; team_manager?: { id?: string | number } | null }) => {
  const ids = new Set((team.members ?? []).map((member) => String(typeof member === 'object' ? member.id : member)))
  if (team.team_manager?.id !== undefined && team.team_manager?.id !== null) ids.add(String(team.team_manager.id))
  return ids.size
}

const TeamTable: React.FC<TableComponentProps> = ({ setData, isUserMode = false }) => {
  const { t } = useTranslation()
  const auth = useContext(AuthContext)
  const currentUser = auth?.user
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const COUNTRY_OPTIONS = useMemo(() => phoneNumbers.map(c => ({ id: c.code, name: c.name })), [])
  const [showDiscardModal, setShowDiscardModal] = useState(false)
  const [isAddTeamsOpen, setIsAddTeamsOpen] = useState(false)

  const tableState = useTableState<RowData>({
    queryKey: 'teams',
    queryFn: ({ page, pageSize, search, filters }) => {
      const mergedFilters = { ...filters }
      if (isUserMode && currentUser?.id) {
        mergedFilters.members = [currentUser.id.toString()]
      }
      return fetchTeamList({
        search,
        page,
        pageSize,
        filters: mergedFilters,
        expand: 'team_manager',
        fields:
          'id,name,country,members,team_manager.id,team_manager.first_name,team_manager.last_name,team_manager.profile_picture,team_manager.work_email',
      })
    },
    itemsPerPage: 15,
  })

  const deleteTeamMutation = useMutation({
    mutationFn: deleteTeam,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teams'] })
    },
  })

  const handleConfirmDelete = () => {
    tableState.selectedRows.forEach((teamId) => {
      deleteTeamMutation.mutate(String(teamId), {
        onSuccess: () => {
          setData((prevData) => prevData.filter((row) => String(row.id) !== teamId))
          queryClient.invalidateQueries({ queryKey: ['teams'] })
        },
      })
    })
    tableState.clearSelection()
    setShowDiscardModal(false)
  }

  const columns: Column[] = [
    ...(!isUserMode ? [{ label: '', accessor: 'select', type: 'select' as const, width: '5%' }] : []),
    {
      label: t('workforce.teams.teamName'),
      accessor: 'name',
      type: 'string',
      width: '25%',
      Cell: ({ row }) => {
        const tone = personTone(String(row.name || ''))
        return (
          <Flex align="center" gap="12px" minW={0}>
            <Flex w="38px" h="38px" flexShrink={0} align="center" justify="center" borderRadius="11px" bg={tone.bg} color={tone.color} fontSize="15px" fontWeight="800">
              {String(row.name || '?').trim().charAt(0).toUpperCase()}
            </Flex>
            <Box minW={0}>
              <Text fontSize="14px" fontWeight="650" color="solid.900" isTruncated>{row.name}</Text>
              <Flex align="center" gap="5px" color="solid.500">
                <UsersRound size={12} />
                <Text fontSize="12px">{t('workforce.teams.peopleCount', { count: teamHeadcount(row) })}</Text>
              </Flex>
            </Box>
          </Flex>
        )
      },
      MobileCell: ({ row }) => (
        <Text {...text.heading} noOfLines={2}>
          {row.name}
        </Text>
      ),
      mobilePriority: 0,
    },
    {
      label: t('workforce.teams.teamManager'),
      accessor: 'team_manager',
      type: 'string',
      Cell: ({ row }) => {
        const managerName = `${row.team_manager?.first_name || ''} ${row.team_manager?.last_name || ''}`.trim()
        if (!managerName) return <Text fontSize="12px" color="solid.300">{t('workforce.teams.noManager', 'No manager')}</Text>
        return <PersonCell image={row.team_manager?.profile_picture} name={managerName} size={30} />
      },
      MobileCell: ({ row }) => {
        const managerName = `${row.team_manager?.first_name || ''} ${row.team_manager?.last_name || ''}`.trim() || t('common.labels.na')
        const displayName = managerName.length > 24 ? `${managerName.slice(0, 21)}...` : managerName

        return (
          <Box display="flex" alignItems="center" justifyContent="flex-end" gap="6px" minW={0} maxW="100%" overflow="hidden">
            <FastAvatar
              boxSize="18px"
              flexShrink={0}
              src={row.team_manager?.profile_picture}
              name={managerName}
            />
            <Text
              fontSize="12px"
              fontWeight="500"
              color="solid.800"
              minW={0}
              maxW="calc(100% - 24px)"
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
              title={managerName}
            >
              {displayName}
            </Text>
          </Box>
        )
      },
      mobilePriority: 1,
    },
    {
      label: t('common.fields.locations'),
      accessor: 'country',
      type: 'custom',
      width: '33%',
      Cell: ({ row }: { row: RowData }) => (
        <Box display="flex" alignItems="center">
          {row.country && (
            <CircleFlag
              countryCode={row.country.toLowerCase()}
              style={{ width: '24px', height: '24px', marginRight: '8px' }}
            />
          )}
          {row.country
            ? <Text fontSize="13px" color="solid.800">{getCountryNameByCode(row.country)}</Text>
            : <Text fontSize="12px" color="solid.300">{t('workforce.people.noCountry', 'Not set')}</Text>}
        </Box>
      ),
    },
    { label: '', accessor: 'actions', type: 'actions' },
  ]

  const handleRowClick = (row: RowData) => {
    const path = isUserMode ? `/user/organization/${row.id}` : `/user/workforce/teams/${row.id}`
    navigate(path)
  }

  const handleOpenNewTab = (row: RowData) => {
    const path = isUserMode ? `/user/organization/${row.id}` : `/user/workforce/teams/${row.id}`
    window.open(path, '_blank')
  }

  const headerActions = (
    <>
      {!isUserMode && tableState.selectedRows.length > 0 ? (
        <Button
          onClick={() => setShowDiscardModal(true)}
          variant="delete"
          size="sm"
        >
          <Icon fill="none" color="white" mr={2}>
            <Trash04Icon />
          </Icon>
          {t('workforce.people.deleteCount', { count: tableState.selectedRows.length })}
        </Button>
      ) : (
        <>
          <FilterButton
            label={t('common.fields.country')}
            options={COUNTRY_OPTIONS}
            onApply={(v) => tableState.updateFilter('country', v)}
          />
          {!isUserMode && (
            <Button
              display={{ base: 'none', md: 'inline-flex' }}
              variant="primary"
              onClick={() => setIsAddTeamsOpen(true)}
              gap="8px"
              size="sm"
            >
              <Icon w="12px" h="12px" viewBox="0 0 24 24" fill="none" color="white">
                <PlusIcon />
              </Icon>
              {t('workforce.teams.addNewTeam')}
            </Button>
          )}
        </>
      )}
    </>
  )

  return (
    <>
      <TableLayout
        tableState={tableState}
        columns={columns}
        tableType="teams"
        onRowClick={handleRowClick}
        onRowIntent={(row) => prefetchTeamRowIntent(queryClient, row.id)}
        onOpenNewTab={handleOpenNewTab}
        headerActions={headerActions}
      />
      {!isUserMode && (
        <>
          <DiscardModal
            show={showDiscardModal}
            handleClose={() => setShowDiscardModal(false)}
            handleDelete={handleConfirmDelete}
            action="delete"
            selectedRowsCount={tableState.selectedRows.length}
          />
          <AddTeamsModal isOpen={isAddTeamsOpen} onClose={() => setIsAddTeamsOpen(false)} />
        </>
      )}
    </>
  )
}

export default TeamTable
